import { Table } from "react-bootstrap";
import ComingTableBody from "./ComingTableBody";
import { useTranslation } from "react-i18next";
import ESpinner from "components/others/Spinner";
import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import type { WarehouseComingRowType } from "./model/types";

interface ComingTableProps {
    aComing: WarehouseComingRowType[];
    setAComing: Dispatch<SetStateAction<WarehouseComingRowType[]>>;
    comingProcess: boolean;
    isLoading?: boolean;
    editable?: boolean;
    showPayment?: boolean;
    /** Максимальна кількість для рядка (напр. залишок заявки) */
    getMaxCount?: (row: WarehouseComingRowType) => number | null | undefined;
}

const ComingTable = ({
    aComing,
    setAComing,
    comingProcess,
    isLoading = false,
    editable = true,
    showPayment = false,
    getMaxCount,
}: ComingTableProps) => {
    const { t } = useTranslation(['Warehouses']);
    const canEdit = editable && !comingProcess;
    const paymentAllRef = useRef<HTMLInputElement>(null);
    const rows = Array.isArray(aComing) ? aComing : [];
    const paidCount = rows.filter((row) => Boolean(row?.coming?.payment)).length;
    const allPaid = rows.length > 0 && paidCount === rows.length;
    const somePaid = paidCount > 0 && paidCount < rows.length;

    useEffect(() => {
        if (paymentAllRef.current) {
            paymentAllRef.current.indeterminate = somePaid;
        }
    }, [somePaid]);

    const buildPaymentDescriptionFromAi = (row: any): string => {
        const aiMeta = row?.coming?.aiMeta;
        if (!aiMeta?.fileName) return String(row?.coming?.paymentDescription || '');
        return `${t('labelAiDocPrefix')}: ${aiMeta.fileName}, ${t('labelAiDocLine')}: ${Number(aiMeta.sourceLine || 0)}`;
    };

    const handleToggleAllPayments = (checked: boolean) => {
        setAComing((prev) =>
            (Array.isArray(prev) ? prev : []).map((row) => {
                if (!checked) {
                    return {
                        ...row,
                        coming: {
                            ...row.coming,
                            payment: false,
                            paymentDescription: '',
                            paymentAttachmentsCloud: [],
                        },
                    };
                }

                const cloudFile = row?.coming?.aiMeta?.cloudFile;
                const existingCloud = Array.isArray(row?.coming?.paymentAttachmentsCloud)
                    ? row.coming.paymentAttachmentsCloud
                    : [];

                return {
                    ...row,
                    coming: {
                        ...row.coming,
                        payment: true,
                        paymentDescription:
                            String(row?.coming?.paymentDescription || '').trim() ||
                            buildPaymentDescriptionFromAi(row),
                        paymentAttachmentsCloud:
                            existingCloud.length > 0
                                ? existingCloud
                                : cloudFile
                                  ? [cloudFile]
                                  : [],
                    },
                };
            }),
        );
    };

    return (<div>

        {!isLoading 
        ?(<div className="crm-table-wrap px-3">
            <Table className="ComingTable table-hover">
                <thead >
                    <tr className="bg-light">
                        <td className="head-name">{t('comingFields.prodName')}</td>
                        <td className="head-price" title={t('labelPrice')}>
                            {t('comingFields.price')}
                        </td>
                        <td className="head-count text-end">{t('comingFields.count')}</td>
                        {showPayment && (
                            <td className="head-payment text-center" title={t('labelPayment')}>
                                <div className="d-inline-flex align-items-center justify-content-center gap-1">
                                    <input
                                        ref={paymentAllRef}
                                        type="checkbox"
                                        className="form-check-input m-0"
                                        checked={allPaid}
                                        disabled={!canEdit || rows.length === 0}
                                        onChange={(e) => handleToggleAllPayments(e.target.checked)}
                                        aria-label={t('labelPayment')}
                                    />
                                    <span>{t('comingFields.payment')}</span>
                                </div>
                            </td>
                        )}
                        <td className="head-action">{t('fields.action')}</td>
                    </tr>
                </thead>

                <ComingTableBody
                    comingProcess={comingProcess}
                    aComing={aComing}
                    setAComing={setAComing}
                    editable={editable}
                    showPayment={showPayment}
                    getMaxCount={getMaxCount}
                />
            </Table>
        </div>)
        :(<ESpinner/>)}
        </div>
    )
}

export default ComingTable;
